"use client";
import {
  PromoTile,
  Drawer,
  Button,
  Link,
  Typography
} from "@shared/ui.primitives";
import { useState, useEffect } from "react";
import * as styles from "./BundlesPromoTile.css";
import { ExperimentFlags } from "@shared/utils.experimentation";
import { useMediaQueries } from "@shared/ui.hooks";
import { trackClick, useSegment } from "tracking";
import { toggle } from "components/SiteChrome/ToggleButton/toggle";

const bundleExperimentText: Record<
  string,
  { title: string; description: string }
> = {
  "0": {
    title: "",
    description: ""
  },
  "1": {
    title: "",
    description: ""
  },
  "2": {
    title: "Build Your Bundle",
    description: "Bundle your favourites in one go - save 15%"
  },
  "3": {
    title: "Build Your Routine",
    description: "Your starter skincare set, made easy - save 15%"
  },
  "-1": {
    title: "",
    description: ""
  }
};

export const BundlesPromoTile = ({
  bundlesExperimentValue,
  isPLP = false
}: {
  bundlesExperimentValue: number;
  isPLP?: boolean;
}) => {
  const [promoDrawerOpen, setPromoDrawerOpen] = useState(false);
  const { isSm } = useMediaQueries();
  const { track } = useSegment();

  useEffect(() => {
    if (isSm && bundlesExperimentValue >= 1) {
      const key = `ExperimentViewed__${ExperimentFlags.PAGE_EXPERIMENT_BUNDLES_FAKE_DOOR}__${bundlesExperimentValue}`;

      if (sessionStorage?.getItem(key)) return;

      track({
        event: "Experiment Viewed",
        properties: {
          experiment_id: ExperimentFlags.PAGE_EXPERIMENT_BUNDLES_FAKE_DOOR,
          experiment_variant: bundlesExperimentValue
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSm, bundlesExperimentValue]);

  if (bundlesExperimentValue < 2 || !isSm) {
    return null;
  }

  const bundleTitle =
    bundleExperimentText[bundlesExperimentValue.toString()].title ?? "";
  const bundleDescription =
    bundleExperimentText[bundlesExperimentValue.toString()].description ?? "";

  const trackingLocationType = isPLP ? "plp" : "mobile_menu";

  return (
    <div className={styles.wrapper[isPLP ? "plp" : "default"]}>
      <PromoTile
        variant="standard"
        layout="padding"
        title={bundleTitle}
        description={bundleDescription}
        buttonText="Create Bundle"
        onClick={() => {
          setPromoDrawerOpen(true);
          track({
            event: "Page Element Clicked",
            properties: {
              module_content: "Bundles Fake Door Create Bundle modal open",
              location_type: trackingLocationType
            }
          });
        }}
        linkAlign="start"
        image={{
          src: "/images/promo-tile/promo-tile-image.jpg",
          altText: "Create Your Own Bundle Image"
        }}
      />
      <Drawer
        isOpen={promoDrawerOpen}
        anchorPosition="bottom"
        size="medium"
        handleDismiss={() => setPromoDrawerOpen(false)}
      >
        <Drawer.Header handleDismiss={() => setPromoDrawerOpen(false)}>
          Thank you for your interest
        </Drawer.Header>
        <Drawer.Body>
          <section
            className={styles.drawerBody}
            data-event-intersection="pageElementViewed"
            data-module-content="Bundles Test Drawer Opened"
            data-location-type={`${isPLP ? "plp" : "Mobile Menu"}`}
          >
            <Typography variant="body-md">
              We’re sorry - the ability to create your own bundle isn’t
              available right now. It’s something we are considering though, so
              your feedback is really appreciated! You can still buy one of our
              kits or any other product you may like.
            </Typography>
            <Typography variant="body-md">
              Thank you for your patience!
            </Typography>
          </section>
        </Drawer.Body>
        <Drawer.Footer>
          <Link href="/shop/kits" width="full" size="none">
            <Button
              width="full"
              variant="primary"
              text="Explore Kits"
              ariaControls="main-menu"
              onClick={(e) => {
                if (!e) return;
                if (!isPLP) {
                  toggle(e.currentTarget);
                }
                trackClick({
                  event: "Page Element Clicked",
                  properties: {
                    module_content: "Bundles Fake Door Explore Kits Link",
                    location_type: trackingLocationType
                  }
                });
                setPromoDrawerOpen(false);
              }}
            />
          </Link>
        </Drawer.Footer>
      </Drawer>
    </div>
  );
};
